import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { LAND_CATEGORY_OPTIONS } from "web_myplann/Portfolio/Forms/LandForm/landCategoryOptions";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { optionalDateAsString } from "web_myplann/Shared/Validation/optionalDateAsString";
import { optionalPositiveInteger } from "web_myplann/Shared/Validation/optionalPositiveInteger";
import { optionalZipCode } from "web_myplann/Shared/Validation/optionalZipCode";
import { trimmedNullable } from "web_myplann/Shared/Validation/trimmedNullable";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import { z } from "zod";

export const getLandFormSchema = (t: TranslateFunction) => {
	const maxMessage = t(Resources.Portfolio.AddShared.Validations.maxValue);
	const positiveIntegerMessages = {
		positiveMessage: t(Resources.Portfolio.AddLand.Validations.positiveInteger),
		maxMessage,
	};
	const requiredMinimumMessage = t(
		Resources.Portfolio.AddLand.Validations.requiredMinimum,
		{
			fields: [
				t(Resources.Portfolio.AddLand.Fields.location),
				t(Resources.Portfolio.AddLand.Fields.locationMunicipality),
				t(Resources.Portfolio.AddLand.Fields.landSize),
				t(Resources.Portfolio.AddLand.Fields.description),
			].join(", "),
		},
	);

	return z
		.object({
			name: trimmedNullable(
				z
					.string()
					.min(1, t(Resources.Portfolio.AddShared.Validations.required))
					.max(
						ValidationLimits.TextLength.LongName,
						t(Resources.Portfolio.AddShared.Validations.maxLength, {
							max: ValidationLimits.TextLength.LongName,
						}),
					),
			),
			location: trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.Location,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.Location,
					}),
				),
			),
			locationMunicipality: trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.Short,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.Short,
					}),
				),
			),
			locationZipCode: optionalZipCode(
				t(Resources.Portfolio.AddLand.Validations.zipFormat),
			),
			parcelNumber: trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.Short,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.Short,
					}),
				),
			),
			propertyCategory: z.nativeEnum(LAND_CATEGORY_OPTIONS).nullable(),
			landSize: optionalPositiveInteger(
				ValidationLimits.Amount.Max,
				positiveIntegerMessages,
			),
			landValue: optionalPositiveInteger(
				ValidationLimits.Amount.Max,
				positiveIntegerMessages,
			),
			purchaseDate: optionalDateAsString(DateFormat.Iso),
			description: trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.Note,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.Note,
					}),
				),
			),
			forInvestments: z.boolean(),
		})
		.superRefine((data, ctx) => {
			const hasMinimum =
				data.location !== null ||
				data.locationMunicipality !== null ||
				data.landSize !== null ||
				data.description !== null;

			if (!hasMinimum) {
				for (const path of [
					"location",
					"locationMunicipality",
					"landSize",
					"description",
				] as const) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						path: [path],
						message: requiredMinimumMessage,
					});
				}
			}
		});
};

export type LandFormInput = z.input<ReturnType<typeof getLandFormSchema>>;
export type LandFormOutput = z.output<ReturnType<typeof getLandFormSchema>>;
